import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PlusIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'

import { getBottomNavigation, ownsPath } from './navigation'
import QuickLogSheet from './QuickLogSheet'

/**
 * Mobile bottom navigation — VH-133
 *
 * Phone-only (`md:hidden`); the tablet rail and desktop sidebar take over
 * above 768px. Four daily destinations sit either side of a raised action
 * button, which is the one genuinely new surface in the redesign — and it
 * only ever routes to screens that already exist.
 *
 * `pb-safe`-style bottom padding via env(safe-area-inset-bottom) keeps the row
 * clear of the iOS home indicator, which otherwise swallows the outer two
 * targets.
 *
 * VH-297 (HP-38): the four come from the one navigation list, an entry stays
 * marked on its sub-pages, and labels are 11px as in the other products.
 */
export default function BottomNav() {
  const { t } = useTranslation()
  const location = useLocation()
  const [sheetOpen, setSheetOpen] = useState(false)
  const items = getBottomNavigation(t)

  // Split around the centre button so the raised control sits in the middle
  // of the row rather than being appended to the end of it.
  const [left, right] = [items.slice(0, 2), items.slice(2)]

  const link = (item) => {
    const active = ownsPath(item, location.pathname)
    return (
      <Link
        key={item.key}
        to={item.phoneHref ?? item.href}
        aria-current={active ? 'page' : undefined}
        className={clsx(
          'flex flex-1 flex-col items-center justify-center gap-0.5 py-2 transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
          active ? 'text-primary-600' : 'text-gray-500 hover:text-gray-900'
        )}
      >
        <item.icon className={clsx('h-6 w-6', active && 'stroke-2')} />
        <span className="text-[11px] font-medium truncate max-w-full px-1">
          {item.name}
        </span>
      </Link>
    )
  }

  return (
    <>
      <nav
        className="md:hidden fixed inset-x-0 bottom-0 z-40 flex items-stretch border-t border-outline-variant bg-surface-container"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        aria-label={t('nav.primary')}
      >
        {left.map(link)}

        {/* Raised centre action. Sits in the flow so the four links stay
            evenly spaced, but is pulled upward to read as primary. */}
        <div className="relative flex w-16 flex-shrink-0 items-start justify-center">
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            aria-label={t('quickLog.title')}
            aria-haspopup="dialog"
            aria-expanded={sheetOpen}
            className="-mt-5 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-on-accent shadow-lg transition-transform active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-surface"
          >
            <PlusIcon className="h-7 w-7" />
          </button>
        </div>

        {right.map(link)}
      </nav>

      <QuickLogSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  )
}
